import React, { useState, useEffect } from 'react';
import {
    BringzzBoxSlider, BringzzCheckoutCard
} from "@bringzz/components";
import classNames from 'classnames';
import { useOrder } from 'context/OrderContext';
import { useBasket } from 'context/BasketContext';
import { useTranslation } from 'react-i18next';
import { DELIVERY_TYPES, getDeliveryTypeLabel } from 'utils/constants';

const BringzzCheckoutPicker = ({ onCardSelect }) => {
    const [selectedCard, setSelectedCard] = useState(0);
    const [deliveryFees, setDeliveryFees] = useState({});
    const [isLoadingFees, setIsLoadingFees] = useState(true);
    const { prescriptionInfo } = useOrder();
    const { fetchInitialFees } = useBasket();
    const { t } = useTranslation();

    const isPrescriptionSet = prescriptionInfo !== null && prescriptionInfo !== undefined;

    // Fetch delivery fees for each delivery type
    useEffect(() => {
        const fetchFees = async () => {
            setIsLoadingFees(true);
            try {
                const fees = await fetchInitialFees();

                setDeliveryFees({
                    [DELIVERY_TYPES.BRINGZZ_PICKUP]: parseFloat(fees.bringzz?.totalFees) || 0,
                    [DELIVERY_TYPES.CLICK_AND_COLLECT]: parseFloat(fees.collect?.totalFees) || 0,
                    [DELIVERY_TYPES.MAIL_DELIVERY]: parseFloat(fees.shipment?.totalFees) || 0
                });
            } catch (error) {
                console.error('Error fetching delivery fees:', error);
            } finally {
                setIsLoadingFees(false);
            }
        };
        fetchFees();
    }, []);

    // Calculate estimated delivery time based on delivery type
    const getEstimatedTime = (deliveryType) => {
        if (isLoadingFees) {
            return '00';
        }
        switch (deliveryType) {
            case DELIVERY_TYPES.BRINGZZ_PICKUP:
                // If prescription exists: customer -> pharmacy -> customer (30-45 mins)
                // If no prescription: pharmacy -> customer (20-30 mins)
                return isPrescriptionSet ? '30-45' : '20-30';
            case DELIVERY_TYPES.CLICK_AND_COLLECT:
                // Time for user to travel to pharmacy
                return '15-20';
            case DELIVERY_TYPES.MAIL_DELIVERY:
                // Courier delivery
                return '1-2';
            default:
                return 'X';
        }
    };

    // Safely format price to 2 decimal places
    const formatPrice = (deliveryType) => {
        if (isLoadingFees) {
            return '00';
        }
        const fee = deliveryFees[deliveryType];
        if (fee === undefined || fee === null) {
            return '0.00';
        }
        const numFee = parseFloat(fee);
        return isNaN(numFee) ? '0.00' : numFee.toFixed(2);
    };

    const handleCardClick = (cardId) => {
        if (cardId === 2 && isPrescriptionSet) {
            return;
        }
        // Removed getBasketForOrderType call - parent component handles basket refresh
        setSelectedCard(cardId);
        onCardSelect(cardId);
    };

    return (
        <BringzzBoxSlider
            behavior={{
                xl: 'center',
                m: 'left'
            }}
            className='space-x-2 px-2 items-center py-3 w-full mb-4'
        >
            <BringzzCheckoutCard
                imageUrl='/basket-service-1.png'
                imageSize='65px'
                title={getDeliveryTypeLabel(DELIVERY_TYPES.BRINGZZ_PICKUP, t)}
                description={t('component.delivery.descriptions.bringzz_pickup', { time: getEstimatedTime(DELIVERY_TYPES.BRINGZZ_PICKUP) })}
                price={formatPrice(DELIVERY_TYPES.BRINGZZ_PICKUP)}
                className={classNames(
                    'cursor-pointer text-center',
                    {
                        'bg-magic-lilac': selectedCard === 0,
                        'bg-sand': selectedCard !== 0,
                        '[&_*]:blur-sm [&_*]:animate-pulse': isLoadingFees
                    }
                )}
                onClick={() => handleCardClick(0)}
            />
            <BringzzCheckoutCard
                imageUrl='/basket-service-2.png'
                imageSize='43px'
                title={getDeliveryTypeLabel(DELIVERY_TYPES.CLICK_AND_COLLECT, t)}
                description={t('component.delivery.descriptions.click_and_collect', { time: getEstimatedTime(DELIVERY_TYPES.CLICK_AND_COLLECT) })}
                price={formatPrice(DELIVERY_TYPES.CLICK_AND_COLLECT)}
                className={classNames(
                    'cursor-pointer text-center',
                    {
                        'bg-magic-lilac': selectedCard === 1,
                        'bg-sand': selectedCard !== 1,
                        '[&_*]:blur-sm [&_*]:animate-pulse': isLoadingFees
                    }
                )}
                onClick={() => handleCardClick(1)}
            />
            <BringzzCheckoutCard
                imageUrl='/basket-service-2.png'
                imageSize='43px'
                title={getDeliveryTypeLabel(DELIVERY_TYPES.MAIL_DELIVERY, t)}
                description={t('component.delivery.descriptions.mail_delivery', { time: getEstimatedTime(DELIVERY_TYPES.MAIL_DELIVERY) })}
                price={formatPrice(DELIVERY_TYPES.MAIL_DELIVERY)}
                className={classNames(
                    'text-center',
                    isPrescriptionSet ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                    {
                        'bg-magic-lilac': selectedCard === 2 && !isPrescriptionSet,
                        'bg-sand': selectedCard !== 2 || isPrescriptionSet,
                        '[&_*]:blur-sm [&_*]:animate-pulse': isLoadingFees && !isPrescriptionSet
                    }
                )}
                onClick={() => handleCardClick(2)}
                disabled={isPrescriptionSet}
            />
        </BringzzBoxSlider>
    );
};

export default BringzzCheckoutPicker;
